import { analyzeWithGemini } from './geminiService';
import type { ModerationResult, Message, Flag } from './types';
import { TRIP_STAGE_RULES, RELATIONSHIP_RULES } from './contextRules';

export interface ModerationContext {
  tripStatus: 'before_pickup' | 'during_ride' | 'after_dropoff';
  location?: string;
  rideDetails?: {
    pickupLocation?: string;
    dropoffLocation?: string;
    estimatedDuration?: number;
    currentStatus?: string;
  };
  previousMessages?: Message[];
  isDefensiveResponse?: boolean;
  previousMessage?: string;
  role?: 'driver' | 'passenger';
  mode?: 'general' | 'transportation';
}

export async function moderateText(text: string, context?: ModerationContext): Promise<ModerationResult> {
  try {
    const { isDefensive, previousFlags, previousMessage, role } = await analyzeResponseContext(text, context);
    
    // If this is a defensive response by a passenger, set special handling flag
    const isPassengerDefensive = isDefensive && role === 'passenger';
    
    // If it's a passenger defensive response, we can skip the API call for clear cases
    if (isPassengerDefensive && text.toLowerCase().trim().match(/^(no|nope|no thanks|no thank you|i can't|i cannot|sorry|not interested)$/i)) {
      console.log('Clear passenger defensive response detected, skipping API call');
      return {
        text,
        flags: [{
          type: 'defensive_response',
          reason: 'Passenger politely declining an inappropriate request',
          confidence: 0.99,
          severity: 'low'
        }],
        overallToxicity: 0.01,
        summary: {
          spamScore: 0.0,
          toxicityScore: 0.01,
          profanityCount: 0,
          emotionalIntensity: 0.1,
          threatLevel: 0.0,
          manipulationScore: 0.0,
          credibilityScore: 1.0,
          contextualRisk: 0.01
        }
      };
    }
    
    const geminiAnalysis = await analyzeWithGemini(text, {
      tripStatus: context?.tripStatus || 'during_ride',
      ...context,
      isDefensiveResponse: isDefensive,
      previousMessage,
      role,
      // Default to general mode unless explicitly set to transportation 
      mode: context?.mode || 'general'
    });
    
    // Determine if this is likely a neutral response
    const isNeutralResponse = isNeutralMessage(text);
    
    // Handle already empty flags list for neutral messages
    let modifiedFlags = geminiAnalysis.flags;
    
    // Special case: If this is a passenger declining something inappropriate, force it to be treated as neutral/defensive
    // This overrides any API classification to ensure passenger defensive responses are never flagged
    if (isPassengerDefensive) {
      // Don't flag the passenger's defensive response, always treat it as appropriate
      modifiedFlags = [{
        type: 'defensive_response',
        reason: 'Passenger appropriately declining an inappropriate request',
        confidence: 0.98,
        severity: 'low'
      }];
    }
    // If there are no flags but the message appears neutral, add a neutral flag
    else if (modifiedFlags.length === 0 && isNeutralResponse) {
      modifiedFlags = [{
        type: 'neutral',
        reason: context?.mode === 'transportation' 
          ? 'The message is neutral and appropriate for a driver-passenger conversation'
          : 'The message is neutral and appropriate',
        confidence: 0.95,
        severity: 'low'
      }];
    } else if (context?.mode === 'transportation') {
      // Only apply transportation-specific processing if in transportation mode
      // Process existing flags
      modifiedFlags = geminiAnalysis.flags.map((flag: Flag) => {
        // Update flag reason to correctly reflect trip context
        if (flag.reason && context?.tripStatus) {
          const contextPhrase = getContextPhrase(context.tripStatus);
          // Replace any mentions of trip context with the correct one
          flag.reason = flag.reason
            .replace(/during a ride/g, contextPhrase)
            .replace(/during the ride/g, contextPhrase)
            .replace(/in a ride/g, contextPhrase)
            .replace(/in the ride/g, contextPhrase);
        }
        
        // For defensive responses from passengers, remove harassment flags and mark as defensive
        if (isDefensive && role === 'passenger') {
          if (['harassment', 'personal_attack', 'explicit_content'].includes(flag.type)) {
            return {
              type: 'defensive_response',
              reason: 'Passenger expressing discomfort with inappropriate behavior',
              confidence: 0.95,
              severity: 'low',
              context: `In response to previous harassment: ${previousMessage}`
            };
          }
        }
        
        // For driver messages, check against relationship rules
        if (role === 'driver') {
          const relationshipRules = RELATIONSHIP_RULES.driver_to_passenger;
          if (relationshipRules.restricted_topics.some(topic => text.toLowerCase().includes(topic))) {
            flag.confidence *= relationshipRules.power_dynamic_multiplier;
            flag.severity = 'high';
            // Increase severity for repeated violations
            if (previousFlags.includes(flag.type)) {
              flag.severity = 'high';
              flag.confidence *= 1.2;
              flag.reason = `Repeated ${flag.type}: ${flag.reason}`;
            }
          }
        }
        
        return flag;
      }).filter(Boolean);
    }

    return {
      text,
      flags: modifiedFlags,
      overallToxicity: isPassengerDefensive ? 0.01 : (isDefensive ? 0.1 : geminiAnalysis.overallToxicity),
      summary: {
        ...geminiAnalysis.summary,
        contextualRisk: isPassengerDefensive ? 0.01 : calculateContextualRisk(modifiedFlags, context)
      }
    };
  } catch (error) {
    console.error('Moderation failed:', error);
    throw error;
  }
}

// Function to determine if a message is likely neutral/appropriate
function isNeutralMessage(text: string): boolean {
  const neutralPatterns = [
    'i am sorry', 'sorry', 'i apologize', 'thank you', 'thanks',
    'hello', 'hi', 'good morning', 'good afternoon', 'good evening',
    'okay', 'ok', 'sure', 'yes', 'no', 'maybe', 'perhaps',
    'have a nice day', 'goodbye', 'see you', 'be there soon',
    'i understand', 'got it', 'understood', 'noted'
  ];
  
  const lowercaseText = text.toLowerCase();
  
  // Check if the message matches any neutral pattern
  return neutralPatterns.some(pattern => lowercaseText.includes(pattern));
}

function getContextPhrase(tripStatus: 'before_pickup' | 'during_ride' | 'after_dropoff'): string {
  switch (tripStatus) {
    case 'before_pickup':
      return 'during the pickup stage';
    case 'during_ride':
      return 'during a ride';
    case 'after_dropoff':
      return 'after dropoff';
    default:
      return 'during the interaction';
  }
}

async function analyzeResponseContext(text: string, context?: ModerationContext): Promise<{isDefensive: boolean; previousFlags: string[]; previousMessage?: string; role?: 'driver' | 'passenger'}> {
  if (!context?.previousMessages?.length) {
    return { isDefensive: false, previousFlags: [], previousMessage: undefined };
  }
  
  const lastMessage = context.previousMessages[context.previousMessages.length - 1];
  const previousFlags = lastMessage.flags?.map(f => f.type) || [];
  const currentRole = context.role || context.previousMessages[context.previousMessages.length - 1]?.role;
  const textLower = text.toLowerCase().trim();
  
  // Special case for very short polite rejections
  const isSimpleRejection = 
    currentRole === 'passenger' && 
    /^(no|nope|no thanks|no thank you|i can't|i cannot|not interested|not now)$/i.test(textLower);
  
  // Check if this is a response to a flagged message
  const lastMessageHasFlag = lastMessage.flags?.some(flag => 
    ['harassment', 'personal_attack', 'explicit_content'].includes(flag.type)
  );
  
  // Check for harassment keywords in previous message (like asking for personal information)
  const previousMessageHasPersonalInfoRequest = 
    /phone|number|contact|address|social|instagram|facebook|snap|meet|location|email|snapchat|whatsapp|telegram|messenger/i.test(lastMessage.content.toLowerCase());
  
  // Check if this is a passenger declining to share personal information (like phone number)
  const isPassengerDeclining = 
    currentRole === 'passenger' && 
    context.previousMessages.length > 0 &&
    /not|don't|won't|no|refuse|decline|can't|never|stop/.test(textLower) &&
    previousMessageHasPersonalInfoRequest;
  
  // Enhanced defensive pattern detection
  const defensivePatterns = [
    'uncomfortable', 'stop', 'inappropriate', 
    'please', 'don\'t', 'not okay',
    'no', 'unwanted', 'offensive',
    'makes me', 'this makes me',
    'really uncomfortable', 'that\'s really',
    'i do not', 'i don\'t', 'i won\'t', 'i can\'t',
    'decline', 'refused', 'refusing', 'not giving',
    'won\'t give', 'won\'t share', 'not interested',
    'thank you', 'thanks', 'sorry'
  ];
  
  const hasDefensivePattern = defensivePatterns.some(pattern => 
    textLower.includes(pattern)
  );
  
  // Determine if the message is defensive - prioritize simple rejections
  const isDefensive = isSimpleRejection || 
                      ((lastMessageHasFlag || isPassengerDeclining) && hasDefensivePattern);

  return { 
    isDefensive, 
    previousFlags,
    previousMessage: lastMessage.content,
    role: currentRole
  };
}

function calculateContextualRisk(flags: Flag[], context?: ModerationContext): number {
  let riskScore = 0;
  
  // Base risk from flags
  flags.forEach(flag => {
    // Skip neutral flags when calculating risk
    if (flag.type === 'neutral') return;
    
    switch (flag.severity) {
      case 'high': riskScore += 0.3; break;
      case 'medium': riskScore += 0.2; break;
      case 'low': riskScore += 0.1; break;
    }
  });

  // Only apply trip stage specific rules if in transportation mode
  if (context?.mode === 'transportation' && context?.tripStatus) {
    const tripRules = TRIP_STAGE_RULES[context.tripStatus];
    if (flags.some(flag => tripRules.high_risk_types.includes(flag.type))) {
      riskScore *= tripRules.severity_multiplier;
    }
  }

  return Math.min(1, riskScore);
}